import React, { useEffect, useState } from 'react';
import { X, Loader2, Crosshair, AlertTriangle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { competitorsAPI } from '../services/api';
import { fmtEur } from './CompetitorPositionBadge';

const BulkAlignDialog = ({ onClose, onAligned }) => {
  const [offset, setOffset] = useState('0.10');
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const off = parseFloat(offset.replace(',', '.'));
    if (Number.isNaN(off) || off < 0) return undefined;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      competitorsAPI.alignPreviewAll(off)
        .then(d => { if (cancelled) return; setItems(d.items); setSelected(prev => (items === null ? new Set(d.items.map(i => i.product_id)) : new Set([...prev].filter(id => d.items.some(i => i.product_id === id))))); })
        .catch(() => { if (!cancelled) toast.error('Aperçu impossible'); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  const toggle = (id) => setSelected(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allChecked = items?.length && selected.size === items.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(items.map(i => i.product_id)));

  const apply = async () => {
    setApplying(true);
    try {
      const r = await competitorsAPI.alignAll([...selected], parseFloat(offset.replace(',', '.')));
      toast.success(`${r.aligned} prix aligné${r.aligned > 1 ? 's' : ''}${r.errors.length ? ` · ${r.errors.length} ignoré${r.errors.length > 1 ? 's' : ''}` : ''}`);
      onAligned?.(r);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Alignement impossible');
    } finally {
      setApplying(false);
    }
  };

  const chosen = (items || []).filter(i => selected.has(i.product_id));
  const totalDrop = chosen.reduce((n, i) => n + (i.current_price - i.new_price), 0);
  const warnCount = chosen.filter(i => i.warnings.length).length;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="bulk-align-dialog">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 py-4 border-b">
          <div>
            <h3 className="font-bold text-gray-900 flex items-center gap-2"><Crosshair size={18} className="text-orange-500" /> Aligner tous les produits plus chers</h3>
            <p className="text-xs text-gray-500 mt-0.5">Chaque prix sera placé juste sous le concurrent le moins cher. Décochez les produits à exclure.</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700" data-testid="bulk-align-close"><X size={18} /></button>
        </div>
        <div className="px-5 py-3 border-b flex flex-wrap items-center gap-4 text-sm">
          <label className="flex items-center gap-2">Écart sous le moins cher (€)
            <Input type="number" step="0.01" min="0" value={offset} onChange={(e) => setOffset(e.target.value)} data-testid="bulk-align-offset" className="w-28" />
          </label>
          {items && <span className="text-gray-600" data-testid="bulk-align-summary"><strong>{chosen.length}</strong> / {items.length} produit{items.length > 1 ? 's' : ''} sélectionné{chosen.length > 1 ? 's' : ''} · baisse cumulée <strong>{fmtEur(totalDrop)}</strong>{warnCount ? <span className="text-amber-700 ml-2 inline-flex items-center gap-1"><AlertTriangle size={13} /> {warnCount} avec avertissement</span> : null}</span>}
          {loading && <Loader2 size={16} className="animate-spin text-orange-500" />}
        </div>
        <div className="overflow-y-auto flex-1">
          {items === null ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-orange-500" /></div>
            : items.length === 0 ? <p className="text-center text-sm text-gray-500 py-12" data-testid="bulk-align-empty">Aucun produit plus cher que la concurrence.</p> : (
              <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="bulk-align-table">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left"><input type="checkbox" checked={!!allChecked} onChange={toggleAll} data-testid="bulk-align-toggle-all" className="rounded" /></th>
                    <th className="px-4 py-2 text-left">Produit</th><th className="px-4 py-2 text-right">Prix actuel</th><th className="px-4 py-2 text-right">Moins cher</th><th className="px-4 py-2 text-right">Nouveau prix</th><th className="px-4 py-2 text-right">Marge HT</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map(i => (
                    <tr key={i.product_id} className={selected.has(i.product_id) ? '' : 'opacity-50'} data-testid={`bulk-align-row-${i.product_id}`}>
                      <td className="px-4 py-2"><input type="checkbox" checked={selected.has(i.product_id)} onChange={() => toggle(i.product_id)} data-testid={`bulk-align-check-${i.product_id}`} className="rounded" /></td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <img src={i.image} alt="" className="w-8 h-8 object-contain bg-gray-50 rounded" />
                          <div><p className="font-medium text-gray-900 truncate max-w-[240px]">{i.name}</p><p className="text-xs text-gray-500">{i.brand} · {i.ref}</p>
                            {i.warnings.map(w => <p key={w} className="text-[11px] text-amber-700 flex items-center gap-1"><AlertTriangle size={11} /> {w}</p>)}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right font-medium">{fmtEur(i.current_price)}</td>
                      <td className="px-4 py-2 text-right">{fmtEur(i.cheapest_price)}<span className="block text-[10px] text-gray-400">C{i.cheapest_slot} · {i.cheapest_host}</span></td>
                      <td className="px-4 py-2 text-right font-bold text-green-700" data-testid={`bulk-align-new-${i.product_id}`}>{fmtEur(i.new_price)}</td>
                      <td className={`px-4 py-2 text-right ${i.margin_pct == null ? 'text-gray-400' : i.margin_pct < 15 ? 'text-red-600 font-medium' : i.margin_pct < 30 ? 'text-amber-700' : 'text-green-700'}`}>{i.margin_pct == null ? '—' : `${i.margin_pct} %`}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2 rounded-b-xl">
          <Button variant="outline" onClick={onClose} data-testid="bulk-align-cancel">Annuler</Button>
          <Button onClick={apply} disabled={!chosen.length || applying || loading} className="bg-green-600 hover:bg-green-700" data-testid="bulk-align-confirm">
            {applying ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />} Aligner {chosen.length} prix
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkAlignDialog;
